import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead from '../../../components/SEO/SEOHead';
import UnifiedNavHeader from '../../../components/navigation/UnifiedNavHeader';
import MobileSportsBar from '../../../components/homePagePanel/MobileSportsBar';
import f1Service from '../../../components/dashboards/services/f1Service';

// Import F1 Analytics Components
import F1DriverStandings from './F1DriverStandings';
import F1ConstructorBattle from './F1ConstructorBattle';
import F1SeasonCalendar from './F1SeasonCalendar';
import F1FastestLaps from './F1FastestLaps';
import F1DriverComparison from './F1DriverComparison';
import F1LiveRaceTracker from './F1LiveRaceTracker';
import F1CircuitStats from './F1CircuitStats';
import F1TeamMatrix from './F1TeamMatrix';
 
// Import NEW Game-Changing Features
import F1TeammateBattle from './F1TeammateBattle';
import F1RaceWeekendTimeline from './F1RaceWeekendTimeline';
import F1ChampionshipCalculator from './F1ChampionshipCalculator';

// Loading Skeleton Component
const DriverCardSkeleton = ({ index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.3 }}
      className="relative h-64 rounded-sm overflow-hidden border border-white/5 bg-white/5"
    >
      <div className="h-full p-5 flex flex-col justify-between">
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="h-3 w-12 bg-white/10 rounded animate-pulse" />
            <div className="h-3 w-8 bg-white/10 rounded animate-pulse" />
          </div>
          <div className="space-y-1">
            <div className="h-5 w-24 bg-white/10 rounded animate-pulse" />
            <div className="h-8 w-32 bg-white/10 rounded animate-pulse" />
          </div>
        </div>
        <div className="space-y-3">
          <div className="h-4 w-28 bg-white/10 rounded animate-pulse" />
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <div className="h-3 w-12 bg-white/10 rounded animate-pulse" />
            <div className="h-3 w-12 bg-white/10 rounded animate-pulse" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Driver Detail View Component
const DriverDetailView = ({ driver, onBack, season = 2025 }) => {
  const [standings, setStandings] = useState([]);
  const [races, setRaces] = useState([]);
  const [allRaces, setAllRaces] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch driver standings and races
  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        setLoadingStats(true);

        // Fetch standings to get driver's current season stats
        const standingsResponse = await f1Service.getDriverStandings(season);
        if (standingsResponse?.response) {
          setStandings(standingsResponse.response);
        }

        // Fetch all races for projection calculations
        const racesResponse = await f1Service.getCurrentSeasonRaces();
        if (racesResponse?.response) {
          setAllRaces(racesResponse.response);

          // Filter to upcoming races only
          const now = new Date();
          const upcomingRaces = racesResponse.response
            .filter(race => new Date(race.date) >= now)
            .slice(0, 4); // Show next 4 races
          setRaces(upcomingRaces);
        }

        setLoadingStats(false);
      } catch (err) {
        console.error('Error fetching driver detail data:', err);
        setLoadingStats(false);
      }
    };

    fetchDriverData();
  }, [driver.id, season]);

  // Find driver's current standing
  const driverStanding = standings.find(s =>
    s.driver?.name?.toLowerCase().includes(driver.lastName.toLowerCase()) ||
    s.driver?.abbr === driver.teamCode
  );

  // Calculate projection data
  const calculateProjection = () => {
    if (!driverStanding || !allRaces.length) return null;

    const now = new Date();
    const completedRaces = allRaces.filter(race => new Date(race.date) < now);
    const upcomingRaces = allRaces.filter(race => new Date(race.date) >= now);
    const racesCompleted = completedRaces.length || 1; // Avoid division by zero
    const racesRemaining = upcomingRaces.length;

    // Average points per race
    const avgPointsPerRace = driverStanding.points / racesCompleted;

    // Projected final points (conservative and optimistic)
    const conservativeProjection = Math.round(driverStanding.points + (avgPointsPerRace * 0.8 * racesRemaining));
    const optimisticProjection = Math.round(driverStanding.points + (avgPointsPerRace * 1.2 * racesRemaining));

    // Championship probability (simple mathematical approach)
    const leader = standings[0];
    const pointsGap = leader.points - driverStanding.points;
    const maxPointsPerRace = 26; // 25 for win + 1 for fastest lap
    const maxPossiblePoints = racesRemaining * maxPointsPerRace;

    let championshipProbability = 0;
    if (driverStanding.position === 1) {
      championshipProbability = 85; // Leader has high probability
    } else if (pointsGap <= maxPossiblePoints) {
      // Mathematical possibility - scale probability based on gap
      const mathematicalChance = ((maxPossiblePoints - pointsGap) / maxPossiblePoints) * 100;
      championshipProbability = Math.max(1, Math.min(50, Math.round(mathematicalChance * 0.6)));
    }

    // Season trend based on championship probability and position
    const trend = championshipProbability > 40 ? 'up' : championshipProbability > 15 ? 'steady' : 'down';

    // Predicted points per race (AI prediction based on trend)
    const trendMultiplier = trend === 'up' ? 1.15 : trend === 'down' ? 0.85 : 1.0;
    const predictedPointsPerRace = (avgPointsPerRace * trendMultiplier).toFixed(1);

    // Points possible in remaining races
    const pointsPossible = Math.round(parseFloat(predictedPointsPerRace) * racesRemaining);

    return {
      avgPointsPerRace: avgPointsPerRace.toFixed(1),
      predictedPointsPerRace,
      pointsPossible,
      conservativeProjection,
      optimisticProjection,
      championshipProbability,
      racesRemaining,
      trend,
      nextRace: upcomingRaces[0]
    };
  };

  const projection = calculateProjection();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen"
    >
      {/* Header Section */}
      <div className="mb-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-wider transition-all duration-150 border border-white/10 hover:border-white/20 text-gray-400 hover:text-white"
        >
          <span>←</span>
          <span>Back to Grid</span>
        </button>

        {/* Driver Header Card */}
        <div
          className="relative rounded-sm overflow-hidden border p-8"
          style={{
            backgroundColor: driver.teamColor.primary,
            borderColor: driver.teamColor.accent,
            boxShadow: `0 8px 32px -8px ${driver.teamColor.primary}60`
          }}
        >
          {/* Background Pattern */}
          <div
            className="absolute inset-0 opacity-[0.02] mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='6.5' numOctaves='1' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
            }}
          />

          {/* Driver Image (if available) */}
          {driver.image && (
            <div className="absolute right-0 bottom-0 h-full w-2/5 overflow-hidden">
              <img
                src={driver.image}
                alt={driver.fullName}
                className="h-full w-full object-contain object-bottom opacity-30"
              />
            </div>
          )}

          <div className="relative z-10">
            {/* Team & Number */}
            <div className="flex items-center justify-between mb-4">
              <div
                className="text-sm font-bold tracking-[0.2em] uppercase"
                style={{ color: `${driver.teamColor.text}cc` }}
              >
                {driver.teamCode} • {driver.team}
              </div>
              <div
                className="text-4xl font-black tabular-nums"
                style={{ color: `${driver.teamColor.text}60` }}
              >
                #{driver.number}
              </div>
            </div>

            {/* Driver Name */}
            <div className="mb-6">
              <div
                className="text-2xl font-medium tracking-tight leading-none"
                style={{ color: `${driver.teamColor.text}cc` }}
              >
                {driver.firstName}
              </div>
              <div
                className="text-6xl font-black tracking-tight leading-none uppercase mt-1"
                style={{ color: driver.teamColor.text }}
              >
                {driver.lastName}
              </div>
            </div>

            {/* Quick Stats Bar */}
            {driverStanding && !loadingStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t" style={{ borderColor: `${driver.teamColor.text}20` }}>
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: `${driver.teamColor.text}80` }}>
                    Position
                  </div>
                  <div className="text-3xl font-black" style={{ color: driver.teamColor.text }}>
                    P{driverStanding.position}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: `${driver.teamColor.text}80` }}>
                    Points
                  </div>
                  <div className="text-3xl font-black" style={{ color: driver.teamColor.text }}>
                    {driverStanding.points}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: `${driver.teamColor.text}80` }}>
                    Wins
                  </div>
                  <div className="text-3xl font-black" style={{ color: driver.teamColor.text }}>
                    {driverStanding.wins || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider mb-1" style={{ color: `${driver.teamColor.text}80` }}>
                    Country
                  </div>
                  <div className="text-2xl font-black" style={{ color: driver.teamColor.text }}>
                    {driver.country}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PickAI Projection Section */}
      {projection && !loadingStats && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-br from-purple-500/10 via-blue-500/5 to-transparent border border-purple-500/20 rounded-sm overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-purple-500/20 bg-purple-500/5">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
                  PickAI Projection
                </h3>
                <span className="text-[10px] text-purple-400 font-mono uppercase tracking-wider ml-auto">
                  Data-Driven Forecast
                </span>
              </div>
            </div>

            {/* Projection Grid */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Championship Probability */}
                <div className="space-y-3">
                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">
                    Championship Probability
                  </div>
                  <div className="space-y-2">
                    <div className="text-3xl font-black text-white tabular-nums">
                      {projection.championshipProbability}%
                    </div>
                    {/* Progress Bar */}
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${projection.championshipProbability}%` }}
                        transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
                        className="h-full rounded-full"
                        style={{
                          background: projection.championshipProbability > 50
                            ? 'linear-gradient(90deg, #10B981, #34D399)'
                            : projection.championshipProbability > 20
                            ? 'linear-gradient(90deg, #F59E0B, #FBBF24)'
                            : 'linear-gradient(90deg, #6B7280, #9CA3AF)'
                        }}
                      />
                    </div>
                    <div className="text-[10px] text-gray-600 font-mono">
                      {projection.championshipProbability > 50
                        ? 'Strong contender'
                        : projection.championshipProbability > 20
                        ? 'Mathematical chance'
                        : projection.championshipProbability > 0
                        ? 'Long shot'
                        : 'Eliminated'}
                    </div>
                  </div>
                </div>

                {/* Next Race Outlook */}
                <div className="space-y-3">
                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">
                    Next Race Outlook
                  </div>
                  <div className="space-y-2">
                    {projection.nextRace ? (
                      <>
                        <div className="text-sm font-bold text-white">
                          {projection.nextRace.competition?.name || 'Grand Prix'}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          {new Date(projection.nextRace.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="mt-2 space-y-1">
                          <div className="text-xs font-mono">
                            <span className="text-violet-400 font-semibold">Avg:</span>{' '}
                            <span className="text-violet-300">{projection.avgPointsPerRace} pts/race</span>
                          </div>
                          <div className="text-xs font-mono">
                            <span className="text-emerald-400 font-semibold">Prediction:</span>{' '}
                            <span className="text-emerald-300 font-bold">{projection.predictedPointsPerRace} pts/race</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-gray-600 font-mono">
                        Season complete
                      </div>
                    )}
                  </div>
                </div>

                {/* Season Trend */}
                <div className="space-y-3">
                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">
                    Season Trajectory
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">
                        {projection.trend === 'up' ? '↗' : projection.trend === 'down' ? '↘' : '→'}
                      </span>
                      <span
                        className={`text-sm font-bold uppercase tracking-wider ${
                          projection.trend === 'up'
                            ? 'text-green-400'
                            : projection.trend === 'down'
                            ? 'text-red-400'
                            : 'text-gray-400'
                        }`}
                      >
                        {projection.trend === 'up' ? 'Trending Up' : projection.trend === 'down' ? 'Trending Down' : 'Holding Steady'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 font-mono">
                      {projection.racesRemaining} races remaining
                    </div>
                  </div>
                </div>

                {/* Projected Final Points */}
                <div className="space-y-3">
                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">
                    Projected Final Total
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl font-black text-white tabular-nums">
                      {projection.conservativeProjection}-{projection.optimisticProjection}
                    </div>
                    <div className="text-[10px] text-gray-600 font-mono">
                      points (range)
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-mono">
                        <span className="text-violet-400 font-semibold">Current:</span>{' '}
                        <span className="text-violet-300">{driverStanding.points} pts</span>
                      </div>
                      <div className="text-xs font-mono">
                        <span className="text-emerald-400 font-semibold">Prediction:</span>{' '}
                        <span className="text-emerald-300 font-bold">
                          {driverStanding.points} + {projection.pointsPossible} pts
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mt-6 pt-4 border-t border-white/5">
                <p className="text-[10px] text-gray-600 font-mono italic">
                  * Projections based on current performance data and race calendar.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Sections Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Races Section */}
        <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
              Upcoming Races
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {loadingStats ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-white/10 rounded animate-pulse" />
                ))}
              </div>
            ) : races.length > 0 ? (
              races.map((race, idx) => (
                <div
                  key={race.id}
                  className="p-3 border border-white/5 rounded-sm hover:border-white/10 transition-colors"
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="text-xs font-bold text-white">
                      {race.competition?.name || 'Grand Prix'}
                    </div>
                    <div className="text-[10px] text-gray-600 font-mono uppercase">
                      Round {race.competition?.location?.round || idx + 1}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    {new Date(race.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {race.circuit?.name}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-xs text-gray-600 font-mono">
                No upcoming races
              </div>
            )}
          </div>
        </div>

        {/* Season Performance Section */}
        <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
              {season} Season Stats
            </h3>
          </div>
          {loadingStats ? (
            <div className="p-4 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white/10 rounded animate-pulse" />
              ))}
            </div>
          ) : driverStanding ? (
            <div className="p-4 space-y-4">
              {/* Championship Position */}
              <div className="flex justify-between items-center pb-3 border-b border-white/5">
                <span className="text-xs text-gray-500 font-mono uppercase tracking-wider">
                  Championship Position
                </span>
                <span className="text-lg font-black text-white tabular-nums">
                  P{driverStanding.position}
                </span>
              </div>

              {/* Total Points */}
              <div className="flex justify-between items-center pb-3 border-b border-white/5">
                <span className="text-xs text-gray-500 font-mono uppercase tracking-wider">
                  Total Points
                </span>
                <span className="text-lg font-black text-white tabular-nums">
                  {driverStanding.points}
                </span>
              </div>

              {/* Gap to Leader */}
              {driverStanding.behind > 0 && (
                <div className="flex justify-between items-center pb-3 border-b border-white/5">
                  <span className="text-xs text-gray-500 font-mono uppercase tracking-wider">
                    Behind Leader
                  </span>
                  <span className="text-lg font-black text-red-400 tabular-nums">
                    -{driverStanding.behind}
                  </span>
                </div>
              )}

              {/* Race Wins */}
              <div className="flex justify-between items-center pb-3 border-b border-white/5">
                <span className="text-xs text-gray-500 font-mono uppercase tracking-wider">
                  Race Wins
                </span>
                <span className="text-lg font-black text-white tabular-nums">
                  {driverStanding.wins || 0}
                </span>
              </div>

              {/* Podiums */}
              {driverStanding.podiums !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 font-mono uppercase tracking-wider">
                    Podium Finishes
                  </span>
                  <span className="text-lg font-black text-white tabular-nums">
                    {driverStanding.podiums}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-xs text-gray-600 font-mono">
              No season stats available
            </div>
          )}
        </div>

        {/* Full-width Components */}
        <div className="lg:col-span-2">
          {/* Fastest Laps Component */}
          <F1FastestLaps season={season} />
        </div>
      </div>
    </motion.div>
  );
};

// Professional Driver Card - Data Terminal Style with Real API Data
const DriverCard = ({ driver, index, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.3 }}
      whileHover={{ y: -4, transition: { duration: 0.15 } }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onClick && onClick(driver)}
      className="relative group cursor-pointer"
    >
      {/* Main Card */}
      <div
        className="relative h-64 rounded-sm overflow-hidden border transition-all duration-200"
        style={{
          backgroundColor: driver.teamColor.primary,
          borderColor: isHovered ? driver.teamColor.accent : `${driver.teamColor.primary}`,
          boxShadow: isHovered
            ? `0 8px 32px -8px ${driver.teamColor.primary}60`
            : '0 2px 8px -4px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Subtle grain texture */}
        <div
          className="absolute inset-0 opacity-[0.02] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='6.5' numOctaves='1' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
          }}
        />

        {/* Driver Image (if available) */}
        {driver.image && !imageError && (
          <div className="absolute right-0 bottom-0 h-full w-2/5 overflow-hidden">
            <motion.img
              src={driver.image}
              alt={driver.fullName}
              className={`h-full w-full object-contain object-bottom transition-opacity duration-300 ${
                imageLoaded ? 'opacity-20' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              animate={{ scale: isHovered ? 1.05 : 1 }}
              transition={{ duration: 0.3 }}
            />
          </div>
        )}

        {/* Content */}
        <div className="relative h-full p-5 flex flex-col justify-between z-10">
          {/* Top - Driver Info */}
          <div className="space-y-2">
            {/* Team Code & Number */}
            <div className="flex items-start justify-between">
              <div
                className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-75"
                style={{ color: driver.teamColor.text }}
              >
                {driver.teamCode}
              </div>
              <div
                className="text-sm font-bold tabular-nums opacity-60"
                style={{ color: driver.teamColor.text }}
              >
                #{driver.number}
              </div>
            </div>

            {/* Driver Name */}
            <div>
              <div
                className="text-lg font-medium tracking-tight leading-none opacity-90"
                style={{ color: driver.teamColor.text }}
              >
                {driver.firstName}
              </div>
              <div
                className="text-3xl font-black tracking-tight leading-none uppercase mt-0.5"
                style={{ color: driver.teamColor.text }}
              >
                {driver.lastName}
              </div>
            </div>

            {/* Career Stats Indicator (on hover) */}
            {driver.stats && driver.stats.worldChampionships > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: isHovered ? 1 : 0, scale: isHovered ? 1 : 0.8 }}
                transition={{ duration: 0.2 }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded"
                style={{ backgroundColor: `${driver.teamColor.text}20` }}
              >
                <span
                  className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: driver.teamColor.text }}
                >
                  {driver.stats.worldChampionships}x Champion
                </span>
              </motion.div>
            )}
          </div>

          {/* Bottom - Metadata */}
          <div className="space-y-3">
            {/* Team Name */}
            <div
              className="text-xs font-semibold uppercase tracking-wider opacity-75"
              style={{ color: driver.teamColor.text }}
            >
              {driver.team}
            </div>

            {/* Data Row */}
            <div className="flex items-center justify-between pt-2 border-t opacity-40" style={{ borderColor: driver.teamColor.text }}>
              <div className="flex items-center gap-1.5">
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: driver.teamColor.text }}
                />
                <span
                  className="text-[10px] font-mono uppercase tracking-wider"
                  style={{ color: driver.teamColor.text }}
                >
                  {driver.country}
                </span>
              </div>
              <div
                className="text-[10px] font-mono uppercase tracking-wider"
                style={{ color: driver.teamColor.text }}
              >
                2025
              </div>
            </div>
          </div>
        </div>

        {/* Hover indicator bar */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: isHovered ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className="absolute bottom-0 left-0 right-0 h-0.5 origin-left"
          style={{ backgroundColor: driver.teamColor.accent }}
        />
      </div>
    </motion.div>
  );
};

// Professional Team Filter
const TeamFilter = ({ team, isActive, onClick, teamColor, teamCode }) => {
  return (
    <button
      onClick={onClick}
      className="relative px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-all duration-150 border"
      style={{
        backgroundColor: isActive ? teamColor : 'transparent',
        color: isActive ? '#FFFFFF' : '#666666',
        borderColor: isActive ? teamColor : '#2a2a2a'
      }}
    >
      {teamCode}
    </button>
  );
};

// Main F1 Dashboard
const F1Dash = () => {
  const [activeTab, setActiveTab] = useState('drivers');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Tab configuration
  const tabs = [
    { id: 'drivers', label: 'Drivers', shortLabel: 'DRV' },
    { id: 'standings', label: 'Standings', shortLabel: 'STD' },
    { id: 'constructors', label: 'Constructors', shortLabel: 'CON' },
    { id: 'calendar', label: 'Calendar', shortLabel: 'CAL' },
    { id: 'weekend', label: 'Race Weekend', shortLabel: 'WKD' },
    { id: 'teammates', label: 'Teammate Battle', shortLabel: 'VS' },
    { id: 'calculator', label: 'What If', shortLabel: 'CALC' },
    { id: 'live', label: 'Live Race', shortLabel: 'LIVE' },
    { id: 'fastest', label: 'Fastest Laps', shortLabel: 'LAP' },
    { id: 'compare', label: 'Compare', shortLabel: 'CMP' },
    { id: 'circuits', label: 'Circuits', shortLabel: 'CIR' },
    { id: 'teams', label: 'Teams', shortLabel: 'TM' }
  ];

  // Fetch drivers data on component mount
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the getCurrentDrivers method which handles 2025 filtering
        const response = await f1Service.getCurrentDrivers();

        if (response && response.response) {
          // Transform each driver
          const transformedDrivers = response.response.map(driver => {
            const transformed = f1Service.transformDriverData(driver);
            transformed.teamColor = f1Service.getTeamColors(transformed.team);
            return transformed;
          });

          // Sort by team name, then by driver number
          const sortedDrivers = transformedDrivers.sort((a, b) => {
            if (a.team === b.team) {
              return a.number - b.number;
            }
            return a.team.localeCompare(b.team);
          });

          setDrivers(sortedDrivers);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching drivers:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchDrivers();
  }, []);

  // Get unique teams from drivers
  const teams = Array.from(new Set(drivers.map(d => d.team)))
    .map(teamName => {
      const driver = drivers.find(d => d.team === teamName);
      return {
        name: teamName,
        code: driver.teamCode,
        color: driver.teamColor.primary
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Filter drivers by selected team
  const filteredDrivers = selectedTeam === 'all'
    ? drivers
    : drivers.filter(d => d.team === selectedTeam);

  // Handle driver card click
  const handleDriverClick = (driver) => {
    setSelectedDriver(driver);
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle back from detail view
  const handleBackToGrid = () => {
    setSelectedDriver(null);
  };

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-black">
        {/* Unified Navigation */}
        <UnifiedNavHeader title="F1 Dashboard" showBackButton={true} />

        {/* Mobile Sports Navigation Bar */}
        <MobileSportsBar />

        <div className="relative px-6 py-8 pb-32">
          <div className="max-w-[1600px] mx-auto">
            {/* Header - Bloomberg Terminal Style */}
            <div className="mb-8 pb-6 border-b border-white/5">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                {/* Title */}
                <h1 className="text-4xl font-black tracking-tight text-white mb-2 uppercase">
                  F1 Dashboard 2025
                </h1>

                {/* Stats Bar */}
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 font-mono">SEASON</span>
                    <span className="text-white font-mono font-semibold">2025</span>
                  </div>
                  <div className="w-px h-4 bg-white/10" />
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 font-mono">VIEW</span>
                    <span className="text-white font-mono font-semibold uppercase">
                      {tabs.find(t => t.id === activeTab)?.label || 'Dashboard'}
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Tab Navigation - Terminal Style */}
            <div className="mb-8">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className="overflow-x-auto pb-2"
              >
                <div className="flex items-center gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-all duration-150 border whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-white/10 text-white border-white/20'
                          : 'bg-transparent text-gray-600 border-white/5 hover:border-white/10 hover:text-gray-400'
                      }`}
                    >
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.shortLabel}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Tab Content - Conditional Rendering */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {/* Drivers Tab */}
                {activeTab === 'drivers' && (
                  <>
                    {/* Show Detail View if driver is selected */}
                    {selectedDriver ? (
                      <AnimatePresence mode="wait">
                        <DriverDetailView
                          key={selectedDriver.id}
                          driver={selectedDriver}
                          onBack={handleBackToGrid}
                          season={2025}
                        />
                      </AnimatePresence>
                    ) : (
                      <>
                        {/* Error State */}
                        {error && (
                          <div className="mb-8 p-4 border border-red-500/20 bg-red-500/10 rounded">
                            <p className="text-sm text-red-400 font-mono">
                              Error loading drivers: {error}
                            </p>
                            <p className="text-xs text-red-400/60 font-mono mt-2">
                              Please check your API configuration and try again.
                            </p>
                          </div>
                        )}

                        {/* Filter Bar */}
                        {!loading && drivers.length > 0 && (
                          <div className="mb-8">
                            <div className="flex items-center gap-2 overflow-x-auto pb-2">
                              <span className="text-[10px] font-mono text-gray-600 uppercase tracking-wider mr-2">
                                Filter
                              </span>
                              <TeamFilter
                                team="All"
                                teamCode="ALL"
                                isActive={selectedTeam === 'all'}
                                onClick={() => setSelectedTeam('all')}
                                teamColor="#E10600"
                              />
                              {teams.map(team => (
                                <TeamFilter
                                  key={team.name}
                                  team={team.name}
                                  teamCode={team.code}
                                  isActive={selectedTeam === team.name}
                                  onClick={() => setSelectedTeam(team.name)}
                                  teamColor={team.color}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Loading State */}
                        {loading && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                            {[...Array(20)].map((_, i) => (
                              <DriverCardSkeleton key={i} index={i} />
                            ))}
                          </div>
                        )}

                        {/* Drivers Grid */}
                        {!loading && drivers.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                            {filteredDrivers.map((driver, index) => (
                              <DriverCard
                                key={driver.id}
                                driver={driver}
                                index={index}
                                onClick={handleDriverClick}
                              />
                            ))}
                          </div>
                        )}

                        {/* Empty State */}
                        {!loading && filteredDrivers.length === 0 && (
                          <div className="flex flex-col items-center justify-center py-24 text-center border border-white/5 rounded-sm">
                            <p className="text-sm text-gray-600 font-mono uppercase tracking-wider">No Data Available</p>
                            <p className="text-xs text-gray-700 font-mono mt-2">
                              {drivers.length === 0 ? 'No drivers found for 2025 season' : 'Try selecting a different team'}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Standings Tab */}
                {activeTab === 'standings' && <F1DriverStandings season={2025} />}

                {/* Constructors Tab */}
                {activeTab === 'constructors' && <F1ConstructorBattle season={2025} maxTeams={10} />}

                {/* Calendar Tab */}
                {activeTab === 'calendar' && <F1SeasonCalendar season={2025} />}

                {/* Race Weekend Timeline Tab */}
                {activeTab === 'weekend' && <F1RaceWeekendTimeline season={2025} />}

                {/* Teammate Battle Tab */}
                {activeTab === 'teammates' && <F1TeammateBattle season={2025} />}

                {/* Championship Calculator Tab */}
                {activeTab === 'calculator' && <F1ChampionshipCalculator season={2025} />}

                {/* Live Race Tab */}
                {activeTab === 'live' && <F1LiveRaceTracker season={2025} />}

                {/* Fastest Laps Tab */}
                {activeTab === 'fastest' && <F1FastestLaps season={2025} />}

                {/* Compare Tab */}
                {activeTab === 'compare' && <F1DriverComparison season={2025} />}

                {/* Circuits Tab */}
                {activeTab === 'circuits' && <F1CircuitStats season={2025} />}

                {/* Teams Tab */}
                {activeTab === 'teams' && <F1TeamMatrix season={2025} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default F1Dash;
